<template>
  <div class="icons-list">
    <template v-for="(item, index) in infoItems" :key="`info-item-${index}`">
      <div
        class="icon-item"
        :class="'icon-item--' + item.type"
        @click="introClick(item.type)"
      >
        <component :is="item.icon" class="icon" />
        <span v-html="item.label.value"></span>
      </div>

      <span class="divider"></span>
    </template>
  </div>
</template>

<script lang="ts" setup>
import IconPeople from '@/public/icons/event/people.svg?component'
import IconDuration from '@/public/icons/event/event-info-duration.svg?component'
import IconLocation from '@/public/icons/event/event-info-location.svg?component'

import type { EventDescription } from '~/composables/types/event'

const props = withDefaults(
  defineProps<{
    eventDetails: EventDescription | null
    isGiftcard: boolean
  }>(),
  {
    eventDetails: null,
    isGiftcard: false,
  }
)
const { t: $t } = useI18n()

const eventData = toRef(() => props.eventDetails)

const introClick = (id) => {
  const el = document.querySelector(`[data-id="${id}"]`)
  if (!el) return

  const offsetHeight = el.getBoundingClientRect().top + window.scrollY
  window.scrollTo({
    // subract 130 to account for navbar
    top: offsetHeight - 130,
    behavior: 'smooth',
  })
}

const getPeopleLabel = computed(() => {
  if (props.isGiftcard)
    return $t('product.show.giftcardIcons.teamEventsAndGiftCards')

  const { max_number_of_people } = eventData.value

  if (!max_number_of_people)
    return $t('product.show.infoBanner.people.notProvided')

  return $t('product.show.infoBanner.people.maxOnly', {
    n: max_number_of_people,
  })
})

const getLocationLabel = computed(() => {
  if (eventData.value.type === 'ONLINE') return 'Online'

  if (eventData.value.has_mobile_classes)
    return $t('product.show.infoBanner.atYourLocation')

  if (eventGetters.getHasMultipleLocations(eventData.value))
    return $t('product.show.giftcardIcons.multipleLocationsSummary')

  return eventData.value?.address?.data?.locality?.data?.name
})

const getDurationLabel = computed(() => {
  if (props.isGiftcard) return $t('product.show.giftcardIcons.durationContent')

  return (
    '<span class="hidden md:inline-block">' +
    $t('product.show.infoBanner.duration') +
    '</span>' +
    ' ' +
    convertMinutesToHumanString(
      +eventGetters.getNextDateAvailableDuration(eventData.value) ||
        +eventGetters.getDefaultDuration(eventData.value) ||
        +eventGetters.getEventMinimumDuration(eventData.value) ||
        +eventGetters.getEventMaximumDuration(eventData.value)
    )
  )
})

const infoItems = computed(() => {
  return [
    {
      type: 'people',
      icon: IconPeople,
      label: getPeopleLabel,
    },
    {
      type: 'location',
      icon: IconLocation,
      label: getLocationLabel,
    },
    {
      type: 'duration',
      icon: IconDuration,
      label: getDurationLabel,
    },
  ]
})
</script>

<style lang="postcss" scoped>
.divider {
  @apply w-[1px] h-auto bg-gray-200 hidden md:block lg:hidden;
  &:last-child {
    @apply hidden;
  }
}
.icons-list {
  @apply w-full flex p-[5px] lg:border border-gray-200 rounded-md gap-2 lg:gap-0 flex-wrap md:flex-nowrap;
  border-radius: 10px;
}

.icon-item {
  @apply border border-gray-200 lg:border-0 lg:border-l md:max-h-[40%] border-gray-200 rounded-lg lg:rounded-none;
  @apply cursor-pointer flex flex-auto px-1.5 py-2.5 md:p-[3px] justify-center items-center text-sm gap-1.5 font-medium text-stone-700;

  &:first-child {
    @apply lg:border-0;
  }

  &--people,
  &--duration {
    @apply whitespace-nowrap;
  }

  .icon {
    @apply min-w-[24px];
  }
}
</style>
